import React from 'react';
import {
  Action, ConfirmModal, Portal, useModal, useNotifications,
} from '@doveit/bricks';
import { Feedback } from '../../../providers/api/dtos';
import { FeedbackStatus } from '../../../domain/types';
import { updateFeedback } from '../../../providers/api/feedback/feedbackProvider';

interface ChildFnProps {
  restoreFeedback: VoidFunction,
  isLoading: boolean,
}

export interface RestoreFeedbackActionProps extends React.AriaAttributes {
  feedback: Feedback,
  onSuccess?: (restoredFeedback: Feedback) => void,
  children?: (props: ChildFnProps) => React.ReactNode,
}

export const RESTORE_FEEDBACK_SUCCESS_MESSAGE = 'La survey è stata ripristinata con successo.';
export const RESTORE_FEEDBACK_ERROR_MESSAGE = 'Non è stato possibile ripristinare la survey.';

const RestoreFeedbackAction: React.FC<RestoreFeedbackActionProps> = ({
  feedback,
  onSuccess,
  children,
  ...rest
}) => {
  const modal = useModal();
  const { addSuccess, addError } = useNotifications();
  const [isSaving, setIsSaving] = React.useState(false);

  const handleRestore = React.useCallback(async () => {
    try {
      setIsSaving(true);

      const feedbackToRestore: Feedback = {
        ...feedback,
        status: FeedbackStatus.TODO,
      };

      const restoredFeedback = await updateFeedback(feedbackToRestore.id!, feedbackToRestore);

      setIsSaving(false);
      addSuccess(RESTORE_FEEDBACK_SUCCESS_MESSAGE);
      modal.close();

      onSuccess?.(restoredFeedback);
    } catch (_) {
      setIsSaving(false);
      addError(RESTORE_FEEDBACK_ERROR_MESSAGE);
    }
  }, [addError, addSuccess, feedback, modal, onSuccess]);

  if (![FeedbackStatus.ARCHIVED, FeedbackStatus.KO_CANNOT_CONTACT].includes(feedback.status)) {
    return null;
  }

  return (
    <>
      {children
        ? children({
          restoreFeedback: modal.open,
          isLoading: isSaving,
        })
        : (
          <Action
            label="Ripristina"
            aria-label="Ripristina la survey"
            size="S"
            onClick={() => modal.open()}
            {...rest}
          />
        )}
      <Portal>
        <ConfirmModal
          isOpen={modal.isOpen}
          saving={isSaving}
          aria-label="Conferma ripristino survey"
          title="Conferma ripristino survey"
          onConfirm={handleRestore}
          onAbort={() => modal.close()}
        >
          Conferma l&apos;operazione per ripristinare la survey.
        </ConfirmModal>
      </Portal>
    </>
  );
};

export default RestoreFeedbackAction;
